import { defineStore } from 'pinia';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import { StoreId } from '@/modules/common/enums/store-id.enum.ts';
import { CLIENTS_COMMENTS_GET } from '@/modules/core/configs/end-points.ts';
import type { CounterpartyComment } from '@/modules/counterparty/types/counterparty-comment.type.ts';
import { withCommentMarkdown } from '@/modules/core/utils/comment-markdown-list.util.ts';
import type { CounterpartyListRequest } from '@/modules/counterparty/types/counterparty-list-request.type.ts';
import GatewayApi from '@/modules/core/api/gateway.api.ts';
import { GatewayFilter } from '@/modules/core/utils/gateway-filter.ts';
import type { ItemsDto } from '@/modules/common/dto/items.dto.ts';

type RawCommentsResponse = { data: ItemsDto<CounterpartyComment> };

function getList(params: CounterpartyListRequest, signal: AbortSignal) {
  return GatewayApi.execute<RawCommentsResponse>(CLIENTS_COMMENTS_GET, {
    filter: GatewayFilter.and(GatewayFilter.eq('CLIENT_ID', params.clientId)),
    limit: params.limit,
    offset: params.offset,
    signal,
    mapData: false,
  });
}

type CounterpartyCommentsState = {
  items: CounterpartyComment[];
  count: number | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: unknown;
  controller: AbortController | null;
};

export const useCounterpartyCommentsStore = defineStore(StoreId.CounterpartyComments, {
  state: (): CounterpartyCommentsState => ({
    items: [],
    count: null,
    isLoading: false,
    isLoadingMore: false,
    error: null,
    controller: null,
  }),
  getters: {
    list: (state) => state.items,
  },
  actions: {
    async fetch(params: CounterpartyListRequest) {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.isLoading = true;
      this.isLoadingMore = false;

      try {
        const response = await getList(params, controller.signal);

        if (this.controller !== controller) {
          return;
        }

        const items = await withCommentMarkdown(
          response.data.items,
          (item: CounterpartyComment) => item.COMMENT_ID,
          controller.signal
        );

        if (this.controller !== controller) {
          return;
        }

        this.items = items;
        if (Number.isInteger(response.data.count)) {
          this.count = response.data.count;
        }
        this.error = null;
      } catch (error) {
        if (this.controller !== controller) {
          return;
        }
        if (ErrorUtil.isForbidden(error) || ErrorUtil.isBusinessError(error)) {
          this.error = error;
          return;
        }
        if (ErrorUtil.isUnexpected(error)) {
          this.error = error;
          throw error;
        }
      } finally {
        if (this.controller === controller) {
          this.isLoading = false;
          this.controller = null;
        }
      }
    },
    async fetchMore(params: CounterpartyListRequest) {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.isLoadingMore = true;
      this.isLoading = false;

      try {
        const response = await getList(params, controller.signal);

        if (this.controller !== controller) {
          return;
        }

        const items = await withCommentMarkdown(
          response.data.items,
          (item: CounterpartyComment) => item.COMMENT_ID,
          controller.signal
        );

        if (this.controller !== controller) {
          return;
        }

        this.items = [...this.items, ...items];
        if (Number.isInteger(response.data.count)) {
          this.count = response.data.count;
        }
        this.error = null;
      } catch (error) {
        if (this.controller !== controller) {
          return;
        }
        if (ErrorUtil.isForbidden(error) || ErrorUtil.isBusinessError(error)) {
          this.error = error;
          return;
        }
        if (ErrorUtil.isUnexpected(error)) {
          this.error = error;
          throw error;
        }
      } finally {
        if (this.controller === controller) {
          this.isLoadingMore = false;
          this.controller = null;
        }
      }
    },
    abort() {
      this.controller?.abort();
      this.isLoading = false;
      this.isLoadingMore = false;
      this.controller = null;
    },
    clear() {
      this.abort();
      this.items = [];
      this.count = null;
      this.error = null;
      this.isLoadingMore = false;
    },
  },
});
